import { Helmet } from "react-helmet";
import type { SharePageMeta } from "../../utils/sharePageMeta";
import { OG_IMAGE_ALT } from "../../utils/shareCopy";
import { resolveOgShareImageUrl } from "../../utils/shareOgImage";

type SharePageHelmetProps = SharePageMeta & {
    /** Defaults to `window.location.href` when set in the browser. */
    url?: string;
    ogType?: "website" | "article";
};

export function SharePageHelmet({
    title,
    description,
    imageAlt = OG_IMAGE_ALT,
    ogGame = "site",
    imageUrl,
    keywords,
    jsonLdMatchName,
    jsonLdApplication,
    jsonLdFaq,
    robots,
    url,
    ogType = "website",
}: SharePageHelmetProps) {
    const resolvedUrl =
        url ?? (typeof window !== "undefined" ? window.location.href : "https://cricket8.com/");
    const image = String(imageUrl ?? "").trim() || resolveOgShareImageUrl(ogGame);
    const matchName = jsonLdMatchName?.trim() || "";
    const jsonLdMatch = matchName
        ? {
              "@context": "https://schema.org",
              "@type": "SportsEvent",
              name: matchName,
              description,
              url: resolvedUrl,
              organizer: {
                  "@type": "Organization",
                  name: "Cricket8",
                  url: "https://cricket8.com/",
              },
          }
        : null;
    const appName = jsonLdApplication?.name?.trim() || "";
    const jsonLdApp =
        !jsonLdMatch && appName
            ? {
                  "@context": "https://schema.org",
                  "@type": "SoftwareApplication",
                  name: appName,
                  description: String(jsonLdApplication?.description ?? description).trim() || description,
                  url: String(jsonLdApplication?.url ?? resolvedUrl).trim() || resolvedUrl,
                  applicationCategory: "GameApplication",
                  operatingSystem: "Web",
                  offers: {
                      "@type": "Offer",
                      price: "0",
                      priceCurrency: "USD",
                  },
                  provider: {
                      "@type": "Organization",
                      name: "Cricket8",
                      url: "https://cricket8.com/",
                  },
              }
            : null;
    const faqItems = Array.isArray(jsonLdFaq)
        ? jsonLdFaq
              .map((item) => ({
                  question: String(item?.question ?? "").trim(),
                  answer: String(item?.answer ?? "").trim(),
              }))
              .filter((item) => item.question && item.answer)
        : [];
    const jsonLdFaqPage =
        faqItems.length > 0
            ? {
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: faqItems.map((item) => ({
                      "@type": "Question",
                      name: item.question,
                      acceptedAnswer: {
                          "@type": "Answer",
                          text: item.answer,
                      },
                  })),
              }
            : null;
    const jsonLdBlocks = [jsonLdMatch || jsonLdApp, jsonLdFaqPage].filter(Boolean);

    return (
        <Helmet>
            <title>{title}</title>
            <meta name="description" content={description} />
            {keywords ? <meta name="keywords" content={keywords} /> : null}
            {robots ? <meta name="robots" content={robots} /> : null}
            <link rel="canonical" href={resolvedUrl} />
            <meta property="og:site_name" content="Cricket8" />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:type" content={ogType} />
            <meta property="og:url" content={resolvedUrl} />
            <meta property="og:image" content={image} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content={imageAlt} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />
            {jsonLdBlocks.map((block, index) => (
                <script key={`jsonld-${index}`} type="application/ld+json">
                    {JSON.stringify(block)}
                </script>
            ))}
        </Helmet>
    );
}

/** Auth / account surfaces that should not be indexed. */
export function NoIndexHelmet() {
    return (
        <Helmet>
            <meta name="robots" content="noindex, nofollow" />
        </Helmet>
    );
}
